/**
 * Confetti burst on add-to-cart.
 *
 * Self-contained canvas particle burst — no external library. A CDN confetti
 * package would add a third-party request on every page for one animation, and
 * would break the storefront if that host ever went down.
 *
 * Triggered by the theme's own `theme-drawer:open` event for the cart drawer.
 * It does NOT wrap window.fetch: this store runs a third-party slide-cart app
 * that intercepts /cart/add, and two scripts wrapping the same global with no
 * guaranteed load order risks breaking the add-to-cart chain.
 */
(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const COLORS = ['#E15B2D', '#F2A65A', '#2B2723', '#F7F1E7', '#1F7A4D'];
  const PARTICLE_COUNT = 90;
  const DURATION = 1600;

  let canvas = null;
  let ctx = null;
  let particles = [];
  let running = false;

  function ensureCanvas() {
    if (canvas) return;
    canvas = document.createElement('canvas');
    canvas.className = 'confetti-cart-canvas';
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '2147483647',
    });
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    resize();
    window.addEventListener('resize', resize);
  }

  function resize() {
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Burst origin: the centre of the cart drawer when it's open, so the effect
   * reads as "this went into your cart" rather than a generic page-wide party.
   * Falls back to the viewport centre if the drawer isn't on screen.
   */
  function getOrigin() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    const panel = drawer && drawer.querySelector('dialog');

    if (panel && panel.open) {
      const rect = panel.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height * 0.3 };
      }
    }

    return { x: window.innerWidth / 2, y: window.innerHeight * 0.35 };
  }

  function spawn(origin) {
    const originX = origin.x;
    const originY = origin.y;
    particles = Array.from({ length: PARTICLE_COUNT }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 4 + Math.random() * 7;
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3,
        size: 5 + Math.random() * 6,
        color: COLORS[(Math.random() * COLORS.length) | 0],
        rotation: Math.random() * Math.PI,
        spin: (Math.random() - 0.5) * 0.3,
        life: 1,
      };
    });
  }

  function frame(start) {
    const elapsed = performance.now() - start;
    const progress = elapsed / DURATION;

    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

    particles.forEach((p) => {
      p.vy += 0.22; // gravity
      p.vx *= 0.99; // drag
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;
      p.life = Math.max(0, 1 - progress);

      ctx.save();
      ctx.globalAlpha = p.life;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      ctx.restore();
    });

    if (progress < 1) {
      requestAnimationFrame(() => frame(start));
    } else {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      particles = [];
      running = false;
    }
  }

  function start(origin) {
    running = true;
    ensureCanvas();
    spawn(origin);
    requestAnimationFrame(() => frame(performance.now()));
  }

  /**
   * The drawer opens asynchronously after the /cart/add response, so poll
   * briefly for it rather than firing immediately at the wrong coordinates.
   * Gives up after ~700ms and bursts at the viewport centre regardless, so the
   * effect still happens on themes/pages where no drawer appears.
   */
  function burst() {
    // Respect the OS-level motion preference — a particle burst is exactly the
    // kind of animation that setting exists to suppress.
    if (REDUCED_MOTION.matches) return;
    if (running) return;

    const deadline = performance.now() + 700;

    const waitForDrawer = () => {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const panel = drawer && drawer.querySelector('dialog');

      if (panel && panel.open) {
        // One more frame so the slide-in has moved it into place.
        requestAnimationFrame(() => start(getOrigin()));
        return;
      }

      if (performance.now() < deadline) {
        requestAnimationFrame(waitForDrawer);
        return;
      }

      start(getOrigin());
    };

    waitForDrawer();
  }

  // Expose for manual triggering / testing.
  window.confettiBurst = burst;

  /**
   * Trigger: the cart drawer opening.
   *
   * This deliberately does NOT wrap window.fetch. An earlier version patched
   * fetch to watch for POST /cart/add, but this store also runs a third-party
   * slide-cart app that intercepts the same request. Two scripts wrapping the
   * same global, with no guaranteed load order, risks breaking the add-to-cart
   * chain — so nothing here sits in that path any more.
   *
   * `theme-drawer:open` is the theme's own event (see theme-drawer.js,
   * DrawerOpenEvent). We only celebrate the cart drawer, not the menu drawer.
   */
  document.addEventListener('theme-drawer:open', (event) => {
    const drawer = event.target;
    if (drawer && drawer.id === 'cart-drawer') burst();
  });
})();
